import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AryLoadingButton from 'framework/base/components/AryLoadingButton';
import AryModal from 'framework/base/components/AryModal';
import {
  ARCHIVE_BRANCH,
  GET_BRANCH_ACCOUNT_STATUS,
  GET_STATE,
  SUBMIT_BALANCE_BASED_BRANCH_CHECKOUT
} from 'framework/base/constants/aryGraphqlSchemas';
import arySystemSettings from 'framework/base/constants/arySystemSettings';
import useAryDelayedMutation from 'framework/base/hooks/useAryDelayedMutation';
import useSystemSettingsContext from 'framework/base/hooks/useSystemSettingsContext';
import { getTimeZoneFormattedDate } from 'framework/base/utils/Utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Stack } from '@mui/system';
import { setShouldRefetchBranches } from 'redux/slices/systemSlice';
import AryBaseTable from 'framework/desktop/BaseTable/AryBaseTable';
import { useAryLazyQuery, useAryMutation } from '../hooks/useAryGraphql';
import { formatMoneyAmount } from './AryNumberFormatter';
import useTableDataParser from '../hooks/useTableDataParser';

const ArchiveAccountCheckout = ({ open, handleClose }) => {
  const { t } = useTranslation();
  const { statusParser, textParser } = useTableDataParser();
  const theme = useTheme();
  const { systemSettings } = useSystemSettingsContext();
  const branchId = useSelector((state) => state.systemReducer.branchId);
  const [branchBalances, setBranchBalances] = useState([]);
  const dispatch = useDispatch();

  const columns = {
    currency: {
      label: t('currency'),
      key: 'currency',
      renderCell: (row, key) => textParser(row, key)
    },
    balance: {
      label: t('BalanceAmount'),
      key: 'balance',
      renderCell: (row, key) => formatMoneyAmount(row[key])
    },
    isDebtor: {
      label: t('Status'),
      key: 'isDebtor',
      renderCell: (row, key) => statusParser(row, key)
    }
  };

  const [
    getBranchAccountStatus,
    {
      data: getBranchAccountStatusQueryData,
      loading: getBranchAccountStatusQueryLoading
    }
  ] = useAryLazyQuery({
    gqlConfig: GET_BRANCH_ACCOUNT_STATUS,
    items: `
      checkoutExists
      checkoutDate
      branchBalances {
        currency
        currencyCode
        debtor
        creditor
        balance
        isDebtor
        creditLimit
        hasUnlimitedLimit
        commissionBalance
        lastCheckoutBalance
        lastCheckoutIsDebtor
        lastCheckoutDebtor
        lastCheckoutCreditor
      }
    `
  });

  useEffect(() => {
    getBranchAccountStatus({
      branchId
    });
  }, [branchId]);

  useEffect(() => {
    if (getBranchAccountStatusQueryData) {
      const branchBalancesArray = getBranchAccountStatusQueryData.branchBalances
        .filter((balance) => balance.balance !== 0 && balance.balance !== '0')
        .map((item) => ({
          ...item,
          isDebtor: !item.isDebtor
        }));

      setBranchBalances(branchBalancesArray);
    }
  }, [getBranchAccountStatusQueryData]);

  const [archiveBranch, { loading: archiveBranchLoading }] =
    useAryMutation(ARCHIVE_BRANCH);

  const [
    submitBalanceBasedBranchCheckout,
    { loading: submitBalanceBasedBranchCheckoutLoading }
  ] = useAryDelayedMutation({
    gqlConfig: SUBMIT_BALANCE_BASED_BRANCH_CHECKOUT,
    type: GET_STATE.type.ACCOUNTING,
    onSucceed: async () => {
      try {
        const response = await archiveBranch(branchId);
        if (response.data) {
          dispatch(setShouldRefetchBranches(true));
        }
      } catch (error) {
        console.error('Archive branch error:', error);
      } finally {
        handleClose();
      }
    }
  });

  const handleCheckout = () => {
    submitBalanceBasedBranchCheckout({
      branchId,
      toDate: String(new Date().getTime()),
      openingBalances: branchBalances.map(
        ({ balance, currencyCode, isDebtor }) => ({
          amount: Math.abs(balance),
          currencyCode,
          isDebtor
        })
      )
    });
  };

  return (
    <AryModal
      open={open}
      handleClose={() => {
        handleClose();
      }}
      title={t('CheckoutAccount')}
      style={{ overflow: 'visible' }}
    >
      <Grid item xs={12}>
        <Grid
          container
          rowSpacing={theme.padding.gridRowSpacing}
          columnSpacing={theme.padding.gridColumnSpacing}
        >
          <Grid container xs={12} gap={theme.padding.gridRowSpacing}>
            <Grid item xs={12} overflow="auto" maxHeight="50vh">
              <AryBaseTable
                columns={columns}
                filteredColumnsList={['currency', 'balance', 'isDebtor']}
                data={branchBalances}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              width="100%"
              justifyContent="flex-end"
              gap={theme.padding.gridColumnSpacing}
            >
              <Typography variant="c2Bold">
                {`${t('To Date')}: `}
                {getTimeZoneFormattedDate(
                  new Date().getTime(),
                  systemSettings[arySystemSettings.isGregorian],
                  systemSettings[arySystemSettings.timeZone],
                  systemSettings[arySystemSettings.isDigitsLatin]
                )}
              </Typography>
              <Stack direction="row" gap={theme.padding.gridColumnSpacing}>
                <AryLoadingButton
                  disabled={getBranchAccountStatusQueryLoading}
                  loading={
                    submitBalanceBasedBranchCheckoutLoading ||
                    archiveBranchLoading
                  }
                  size="medium"
                  variant="contained"
                  fullWidth
                  sx={{ height: theme.componentSizes.inputBoxHeight }}
                  onClick={handleCheckout}
                >
                  {t('SubmitNewCheckout')}
                </AryLoadingButton>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </AryModal>
  );
};

export default ArchiveAccountCheckout;
